import React, { useState } from "react";
import { useAuth, formatApiError } from "../lib/auth";
import { toast } from "sonner";
import { Backspace } from "@phosphor-icons/react";

/**
 * Login page — sized so it ALWAYS fits inside the current viewport with
 * zero scrolling on any tablet (portrait or landscape). Key techniques:
 *
 *   1. Root uses `h-[100dvh]` so we lock to the *actual visible* screen,
 *      not the desktop 100vh (which ignores the browser chrome on iOS
 *      and Android).
 *   2. Landscape (min-aspect-ratio > 4:3) → two-column grid: brand mark
 *      on the left, sign-in card on the right. Keypad is roomy but the
 *      logo doesn't push everything off-screen.
 *   3. Portrait (default) → single column with a much smaller logo and
 *      a keypad button height driven by the viewport (`h-[10svh]`) so
 *      it shrinks on short screens instead of overflowing.
 *   4. Everything sits inside a max-height flex column that flexes the
 *      keypad — never the outer page.
 */

const WILSON_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_leica-control/artifacts/u5m03uje_Wilson%20Design%202019.webp";

const KeypadBtn = ({ digit, onPress, testid, children }) => (
  <button
    type="button"
    data-testid={testid || `keypad-${digit}`}
    onClick={() => onPress(digit)}
    // Height scales with viewport: shrinks on short screens, roomy on
    // tall ones — never causes the page to overflow.
    className="flex h-[9svh] min-h-[44px] max-h-[68px] items-center justify-center rounded-lg border border-[#3a3e49] bg-gradient-to-br from-[#2a2d35] to-[#23252c] text-2xl font-bold text-white shadow-[3px_3px_6px_#07080a,-3px_-3px_6px_#1c1f26] transition-all duration-150 active:translate-y-[1px] active:from-[#23252c] active:to-[#262931] active:shadow-[inset_2px_2px_4px_#07080a,inset_-2px_-2px_4px_#1c1f26]"
    style={{ fontFamily: "Chakra Petch, sans-serif" }}
  >
    {children ?? digit}
  </button>
);

const BrandBadge = () => (
  <div className="flex flex-col items-center justify-center gap-3">
    <img
      src="/fabaxis-logo.png"
      alt="FabAxis"
      data-testid="fabaxis-logo"
      // portrait  → 2-3× smaller so the keypad doesn't push off-screen
      // landscape → grows into the left column
      className="h-24 w-auto max-w-full landscape:h-40"
    />
    <div className="hidden text-center landscape:block">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">
        Wireless Field Controller
      </div>
      <div className="mt-1 text-[9px] uppercase tracking-[0.3em] text-[#4A5054]">
        Leica TS12 / TS16
      </div>
    </div>
    {/* Wilson chrome mark — kept as a small easter egg */}
    <div
      className="mt-2 h-5 w-20 opacity-60"
      style={{
        maskImage: `url(${WILSON_LOGO})`,
        WebkitMaskImage: `url(${WILSON_LOGO})`,
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskPosition: "center",
        background:
          "linear-gradient(to bottom, #ffffff 0%, #a6aeb5 35%, #5a6065 50%, #8b939a 55%, #e2e5e8 100%)",
        filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.8))",
      }}
    />
  </div>
);

const SignInCard = ({ username, setUsername, passcode, press, backspace, busy }) => (
  <div className="mx-auto flex w-full max-w-sm flex-col rounded-xl border border-[#2A2E39] bg-[#14161A] p-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] sm:p-5">
    <div className="mb-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">
        Field Controller
      </div>
      <div
        className="mt-0.5 text-xl font-bold uppercase tracking-tight text-white sm:text-2xl"
        style={{ fontFamily: "Chakra Petch, sans-serif" }}
      >
        Sign In
      </div>
    </div>

    <label className="mb-3 block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
        Username
      </span>
      <input
        data-testid="login-username"
        type="text"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="e.g. Andy"
        className="h-11 w-full rounded-md border border-[#3a3e49] bg-[#0A0B0E] px-3 text-base text-white focus:border-[#009E8E] focus:outline-none"
        style={{ fontFamily: "JetBrains Mono, monospace" }}
      />
    </label>

    <div>
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
        4-digit passcode
      </span>
      {/* Passcode dots */}
      <div className="mb-3 flex justify-center gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            data-testid={`passcode-dot-${i}`}
            className={`h-3.5 w-3.5 rounded-full border-2 transition-colors ${
              passcode.length > i
                ? "border-[#8DC63F] bg-[#8DC63F] shadow-[0_0_8px_#8DC63F]"
                : "border-[#3a3e49] bg-transparent"
            }`}
          />
        ))}
      </div>
      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <KeypadBtn key={d} digit={String(d)} onPress={press} />
        ))}
        <div />
        <KeypadBtn digit="0" onPress={press} />
        <button
          type="button"
          data-testid="keypad-backspace"
          onClick={backspace}
          className="flex h-[9svh] min-h-[44px] max-h-[68px] items-center justify-center rounded-lg border border-[#3a3e49] bg-[#1A1D24] text-[#8B939A] transition-colors active:bg-[#2A2E39]"
        >
          <Backspace size={22} weight="bold" />
        </button>
      </div>
    </div>

    <div className="mt-2 min-h-[16px] text-center text-[10px] uppercase tracking-widest text-[#009E8E]">
      {busy ? "Authenticating…" : ""}
    </div>
  </div>
);

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);

  const press = (d) => {
    if (passcode.length >= 4) return;
    const next = passcode + d;
    setPasscode(next);
    if (next.length === 4) submit(username, next);
  };

  const backspace = () => setPasscode((p) => p.slice(0, -1));

  const submit = async (u, p) => {
    if (!u.trim()) {
      toast.error("Enter your username first");
      setPasscode("");
      return;
    }
    setBusy(true);
    try {
      await login(u.trim(), p);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Login failed");
      setPasscode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex h-[100dvh] w-full flex-col items-center justify-center gap-4 overflow-hidden bg-[#0A0B0E] p-4 sm:gap-6 sm:p-6 landscape:grid landscape:grid-cols-[1fr_minmax(320px,420px)] landscape:items-center"
      data-testid="login-page"
    >
      {/* Radial glow — subtle, behind everything */}
      <div
        className="pointer-events-none fixed inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(600px circle at 50% 30%, #005B9F 0%, transparent 70%)",
        }}
      />

      {/* Brand column — grows in landscape, shrinks in portrait */}
      <div className="relative z-10 flex items-center justify-center">
        <BrandBadge />
      </div>

      {/* Sign-in column */}
      <div className="relative z-10 flex items-center justify-center">
        <SignInCard
          username={username}
          setUsername={setUsername}
          passcode={passcode}
          press={press}
          backspace={backspace}
          busy={busy}
        />
      </div>
    </div>
  );
}
